
import { Options, prop, Vue } from "vue-class-component";
import { debounce } from "lodash";
import {
  CommodityBookingV4,
  ErrorBookingV4
} from "@/domain/entities/BookingV4";
import { ellipsisString } from "@/app/infrastructures/misc/Utils";
import Tooltip from "@/app/ui/components/tooltip/index.vue";
import OverlayPanel from "primevue/overlaypanel";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";

class Props {
  modelValue = prop<CommodityBookingV4 | null>({
    required: true,
    default: null
  });
  errors = prop<ErrorBookingV4>({
    required: true,
    default: () => new ErrorBookingV4(),
    type: Object
  });
  recommendedCommodities = prop<CommodityBookingV4[]>({
    required: true,
    default: () => []
  });
  commodities = prop<CommodityBookingV4[]>({
    required: true,
    default: () => []
  });
  isForeign = prop<boolean>({
    required: true,
    default: false,
    type: Boolean
  });
  loading = prop<boolean>({ default: false, type: Boolean });
  hasMore = prop<boolean>({ default: false, type: Boolean });
  // Adjust v4 per-field rule (GENESIS-20182) — optional, default EDITABLE
  configCommodity = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
}

@Options({
  name: "CommoditySectionV4",
  components: {
    Tooltip,
    OverlayPanel
  },
  emits: [
    "update:modelValue",
    "search",
    "loadMore",
    "openModal",
    "validationChange",
    "commodityError"
  ],
  watch: {
    modelValue() {
      this.emitValidationChange();
    }
  }
})
class CommoditySectionV4 extends Vue.with(Props) {
  isOpenTooltipCommodity = false;

  get configValidationForm() {
    return ConfigValidationForm;
  }

  get disabledCommodity(): boolean {
    return this.configCommodity.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  mounted() {
    this.emitValidationChange();
  }

  onInputFocus() {
    if (!this.commodities.length) {
      this.$emit("search", "");
    }
  }

  async onInputBlur() {
    await this.$nextTick();

    const isEmpty =
      !this.modelValue ||
      typeof this.modelValue === "string" ||
      (!this.modelValue.code && !this.modelValue.id);

    if (isEmpty) {
      const errorMsg = "bookingV4.goods.commodity.errorRequired";
      this.$emit("commodityError", errorMsg);
    } else {
      this.$emit("commodityError", "");
    }
  }

  toggleTooltip(event: any, code: string) {
    const refs: any = this.$refs;
    refs[code].toggle(event);
  }

  emitValidationChange() {
    this.$emit("validationChange", !!this.modelValue?.code);
  }

  displayName(item: CommodityBookingV4): string {
    if (!item) return "";
    if (this.isForeign && item.nameEn) return this.toSentenceCase(item.nameEn);
    return this.toSentenceCase(item.name || "");
  }

  toSentenceCase(val: string): string {
    if (!val) return "";
    const isAllUpper = val === val.toUpperCase() && val !== val.toLowerCase();
    if (!isAllUpper) return val;

    return val
      .toLowerCase()
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");
  }

  ellipsisText(val: string, length: number) {
    return ellipsisString(val, length);
  }

  get searchOptions() {
    return this.commodities.map((item) => ({
      ...item,
      label: this.displayName(item)
    }));
  }

  get selectedOption() {
    if (!this.modelValue) return null;
    return {
      ...this.modelValue,
      label: this.displayName(this.modelValue)
    };
  }

  set selectedOption(value: any) {
    if (!value) {
      this.$emit("update:modelValue", null);
      return;
    }

    this.$emit("commodityError", "");

    const found = this.commodities.find(
      (item) => item.code === value.code || (item.id && item.id === value.id)
    );

    const commodity = found
      ? found
      : value instanceof CommodityBookingV4
      ? value
      : new CommodityBookingV4(value);

    this.$emit("update:modelValue", commodity);
  }

  currentSelected(slotProps: any): CommodityBookingV4 | null {
    if (slotProps && slotProps.selected) return slotProps.selected;
    if (slotProps && slotProps.item) return slotProps.item;
    return this.modelValue;
  }

  emitSearch = debounce((keyword: string) => {
    this.$emit("search", keyword);
  }, 250);

  onSearch(keyword: string) {
    this.emitSearch(keyword);
  }

  onLoadMore(keyword: string) {
    this.$emit("loadMore", keyword);
  }

  onSelectChange(item: any) {
    if (item) {
      this.$emit("commodityError", "");
    }
  }

  onClearSearch() {
    if (this.disabledCommodity) return;

    this.$emit("update:modelValue", null);

    const errorMsg = "bookingV4.goods.commodity.errorRequired";
    this.$emit("commodityError", errorMsg);

    this.$emit("validationChange", false);
    this.$emit("search", "");
  }

  onChoose(recommend: CommodityBookingV4) {
    if (this.disabledCommodity) return;

    const target =
      recommend instanceof CommodityBookingV4
        ? recommend
        : new CommodityBookingV4(recommend);

    this.$emit("update:modelValue", target);
    this.$emit("commodityError", "");
  }

  isSelectedRecommendation(recommend: CommodityBookingV4): boolean {
    if (!this.modelValue) return false;
    return (
      (!!this.modelValue.code && this.modelValue.code === recommend.code) ||
      (!!this.modelValue.id && this.modelValue.id === recommend.id)
    );
  }

  onOpenModal() {
    if (this.disabledCommodity) return;
    this.$emit("openModal");
  }
}

export default CommoditySectionV4;
